import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCurtailmentConfig, getCurtailments, createCurtailment,
  cancelCurtailment, deleteCurtailment, getForemanStatus,
  getCurtailmentLocations, setCurtailmentDryRun, resumeCurtailment,
  getCurtailmentEmailConfig, saveCurtailmentEmailConfig, testCurtailmentEmail,
} from '../api';
import { groupBySite } from '../utils/siteGroups';
import MinerFleet from './MinerFleet';

// Curtailment log + scheduler. Entries fire the Foreman curtail/resume calls
// at start/end time on the backend; while the backend's DRYRUN_CURTAILMENT
// switch is on, those calls are recorded on the entry instead of sent.

// Last known locations list survives reloads and backend restarts — the
// picker shows it instantly and only a non-empty fresh fetch replaces it.
const LOC_CACHE_KEY = 'power.curtailLocations';

function loadCachedLocations() {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOC_CACHE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function pad(n) { return String(n).padStart(2, '0'); }

function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Default start = right now, so "log curtailment" doubles as "curtail now";
// bump the field forward to schedule for later instead.
function defaultStart() {
  return toLocalInput(new Date());
}

function plusHours(localStr, h) {
  const d = new Date(localStr);
  d.setHours(d.getHours() + h);
  return toLocalInput(d);
}

const TIME_OPTS = { hour: 'numeric', minute: '2-digit' };
const DAY_OPTS = { weekday: 'short', month: 'short', day: 'numeric' };

// "Thu, Jul 16 · 5:00 PM → 9:00 PM" (same day) or full dates on both sides;
// open-ended entries render "→ until resumed"
function fmtRange(startStr, endStr) {
  const s = new Date(startStr);
  if (!endStr) {
    if (isNaN(s)) return `${startStr} → until resumed`;
    return `${s.toLocaleDateString(undefined, DAY_OPTS)} · ${s.toLocaleTimeString(undefined, TIME_OPTS)} → until resumed`;
  }
  const e = new Date(endStr);
  if (isNaN(s) || isNaN(e)) return `${startStr} → ${endStr}`;
  const sameDay = s.toDateString() === e.toDateString();
  const sDay = s.toLocaleDateString(undefined, DAY_OPTS);
  const sTime = s.toLocaleTimeString(undefined, TIME_OPTS);
  const eTime = e.toLocaleTimeString(undefined, TIME_OPTS);
  if (sameDay) return `${sDay} · ${sTime} → ${eTime}`;
  return `${sDay} ${sTime} → ${e.toLocaleDateString(undefined, DAY_OPTS)} ${eTime}`;
}

function fmtStamp(str) {
  const d = new Date(str);
  if (isNaN(d)) return str;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, TIME_OPTS)}`;
}

function foremanSummary(e, dryRun) {
  const down = e.foreman?.down;
  const resume = e.foreman?.resume;
  if (!down) return e.status === 'scheduled' ? `Foreman fires at start${dryRun ? ' (dry run: logged only)' : ''}` : null;
  const part = (calls, verb) => {
    if (!calls) return null;
    const staged = calls.filter(c => c.method === 'staged-ramp');
    if (staged.length) {
      const all = staged.flatMap(c => c.batches || []);
      const sent = all.filter(b => b.sent_at).length;
      const skipped = all.filter(b => b.skipped).length;
      const state = sent + skipped >= all.length
        ? (skipped ? ` (${skipped} step${skipped === 1 ? '' : 's'} skipped)` : ' — done')
        : ' — ramping';
      return `${verb} ramp ${sent}/${all.length} steps${state}`;
    }
    const sent = calls.filter(c => c.sent).length;
    return sent > 0 ? `${verb} sent (${sent}/${calls.length})` : `${verb} logged, not sent`;
  };
  return ['Foreman:', part(down, 'curtail'), resume ? '·' : null, part(resume, 'resume')]
    .filter(Boolean).join(' ');
}

export default function CurtailmentPanel() {
  const [config, setConfig] = useState(null);
  const [locations, setLocations] = useState(loadCachedLocations);
  const [locError, setLocError] = useState('');
  const [locSearch, setLocSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(() => plusHours(defaultStart(), 3));
  const [noEnd, setNoEnd] = useState(false);
  const [dryRunEntry, setDryRunEntry] = useState(true); // always re-checks after each log
  const [selectedSites, setSelectedSites] = useState([]);
  const [customSites, setCustomSites] = useState('');
  const [reason, setReason] = useState('MISO peak');
  const [customReason, setCustomReason] = useState('');
  const [note, setNote] = useState('');
  const [deadline, setDeadline] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [foremanStatus, setForemanStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailStatus, setEmailStatus] = useState('');

  const refresh = () => {
    getCurtailments().then(d => setEntries(d.results || [])).catch(() => {});
  };

  useEffect(() => {
    getCurtailmentConfig().then(setConfig).catch(() => setConfig({ dry_run: true, sites: [] }));
    // Right after a backend restart the Foreman/eGauge caches are cold and the
    // first locations call can come back empty — keep showing the cached list
    // and retry a few times; only a non-empty result ever replaces it.
    let cancelled = false;
    let tries = 0;
    const loadLocations = () => {
      if (cancelled) return;
      getCurtailmentLocations()
        .then(d => {
          if (cancelled) return;
          const locs = d.locations || [];
          if (locs.length) {
            setLocations(locs);
            setLocError('');
            try { localStorage.setItem(LOC_CACHE_KEY, JSON.stringify(locs)); } catch {}
          } else {
            setLocError(prev => loadCachedLocations().length ? prev : (d.error || ''));
            if (tries++ < 3) setTimeout(loadLocations, 8000);
          }
        })
        .catch(e => {
          if (cancelled) return;
          if (!loadCachedLocations().length) setLocError(String(e.message || e));
          if (tries++ < 3) setTimeout(loadLocations, 8000);
        });
    };
    loadLocations();
    getCurtailmentEmailConfig()
      .then(d => { setEmailCfg(d); setEmailInput((d.recipients || []).join(', ')); })
      .catch(() => {});
    refresh();
    const t = setInterval(refresh, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // While a curtailment window is live, poll entries fast enough to watch
  // the 30-second ramp batches flip in the progress modal.
  const hasActive = entries.some(e => e.status === 'active');
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [hasActive]);

  const startNow = () => {
    const s = defaultStart();
    setStart(s);
    if (!noEnd && new Date(end) <= new Date(s)) setEnd(plusHours(s, 3));
  };

  const saveEmails = async () => {
    setEmailStatus('saving…');
    try {
      const d = await saveCurtailmentEmailConfig(
        emailInput.split(',').map(s => s.trim()).filter(Boolean));
      setEmailCfg(d);
      setEmailInput((d.recipients || []).join(', '));
      setEmailStatus('saved ✓');
    } catch (e) {
      setEmailStatus(String(e?.response?.data?.detail || e.message || e));
    }
  };

  const sendTestEmail = async () => {
    setEmailStatus('sending test…');
    try {
      const d = await testCurtailmentEmail();
      setEmailStatus(`test sent via ${d.transport} ✓`);
    } catch (e) {
      setEmailStatus(String(e?.response?.data?.detail || e.message || e));
    }
  };

  const toggleSite = (s) => {
    setSelectedSites(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleExpand = (g) => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  // Native <details> only closes via its summary; close the location picker
  // on any click elsewhere on the page too.
  const locPickerRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      const el = locPickerRef.current;
      if (el && el.open && !el.contains(e.target)) el.open = false;
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const siteGroups = useMemo(() => groupBySite(locations), [locations]);

  // Filter matches on site name (whole group) or member name (those members)
  const shownGroups = useMemo(() => {
    const q = locSearch.trim().toLowerCase();
    if (!q) return siteGroups;
    return siteGroups
      .map(([g, members]) => {
        if (g.toLowerCase().includes(q)) return [g, members];
        const m = members.filter(x => x.toLowerCase().includes(q));
        return m.length ? [g, m] : null;
      })
      .filter(Boolean);
  }, [siteGroups, locSearch]);

  const shownMembers = useMemo(() => shownGroups.flatMap(([, m]) => m), [shownGroups]);

  const toggleGroup = (members) => {
    setSelectedSites(prev => {
      const allIn = members.every(m => prev.includes(m));
      if (allIn) return prev.filter(x => !members.includes(x));
      return Array.from(new Set([...prev, ...members]));
    });
  };

  const submit = async () => {
    setFormError('');
    const sites = [
      ...selectedSites,
      ...customSites.split(',').map(s => s.trim()).filter(Boolean),
    ];
    if (!noEnd && new Date(end) <= new Date(start)) {
      setFormError('End must be after start.');
      return;
    }
    if (!dryRunEntry && !window.confirm(
      'Dry run is UNCHECKED — the selected locations will really curtail at start. Continue?')) {
      return;
    }
    setSubmitting(true);
    try {
      // datetime-local values are the user's wall clock with no zone info —
      // convert to absolute instants so scheduling is correct regardless of
      // the user's timezone vs the server's (EST user, Central server, …).
      const created = await createCurtailment({
        start: new Date(start).toISOString(),
        end: noEnd ? null : new Date(end).toISOString(),
        sites,
        reason: reason === 'Other' ? (customReason.trim() || 'Other') : reason,
        note,
        deadline_minutes: Number(deadline) || 10,
        dry_run: dryRunEntry,
      });
      if (created?.retroactive) {
        setFormError('Heads up: that window is entirely in the past, so it was logged '
          + 'for the record only — no miners were or will be curtailed by it.');
      }
      setNote('');
      setCustomReason('');
      setCustomSites('');
      setSelectedSites([]);
      setNoEnd(false);
      setDryRunEntry(true); // safety: always back to dry run for the next one
      refresh();
    } catch (e) {
      setFormError(String(e?.response?.data?.detail || e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async (id) => {
    if (!window.confirm('Cancel this curtailment?')) return;
    try { await cancelCurtailment(id); } finally { refresh(); }
  };

  const doResume = async (id) => {
    if (!window.confirm('Resume now? This ends the curtailment and fires the Foreman revert (or logs it, if dry run).')) return;
    try { await resumeCurtailment(id); }
    catch (e) { setFormError(String(e?.response?.data?.detail || e.message || e)); }
    finally { refresh(); }
  };

  const toggleMaster = async () => {
    const armingLive = config?.dry_run !== false; // currently dry → arming
    if (armingLive && !window.confirm(
      'Turn OFF the master dry-run switch? Entries logged with their own dry-run box unchecked will fire REAL Foreman commands.')) {
      return;
    }
    try { setConfig(await setCurtailmentDryRun(!armingLive ? true : false)); }
    catch (e) { setFormError(String(e?.response?.data?.detail || e.message || e)); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this log entry? This cannot be undone.')) return;
    try { await deleteCurtailment(id); } finally { refresh(); }
  };

  const checkForeman = async () => {
    setChecking(true);
    try { setForemanStatus(await getForemanStatus()); }
    catch (e) { setForemanStatus({ ping: { ok: false, error: String(e.message || e) } }); }
    finally { setChecking(false); }
  };

  const dryRun = config?.dry_run !== false;
  const minerCount = Array.isArray(foremanStatus?.miners_summary?.data)
    ? foremanStatus.miners_summary.data.length : null;

  return (
    <>
    <div className="panel">
      <div className="curtail-head">
        <h2 style={{ margin: 0 }}>Curtailment</h2>
        <span className={`badge ${dryRun ? 'warning' : 'danger'}`} title={dryRun
          ? 'Master dry-run is ON: every entry logs only, nothing is sent to Foreman regardless of its own checkbox.'
          : 'Master dry-run is OFF: each entry\'s own dry-run checkbox decides whether Foreman commands really fire.'}>
          {dryRun ? 'MASTER DRY RUN' : 'ARMED'}
        </span>
        <span className="curtail-subnote">
          {dryRun ? 'everything logs only' : 'entries with dry run unchecked fire real commands'}
        </span>
        {config?.is_admin && (
          <label className="curtail-live-toggle" title="Master safety switch (admin only)">
            <input type="checkbox" checked={dryRun} onChange={toggleMaster} />
            master dry run
          </label>
        )}
        <div className="curtail-head-right">
          {foremanStatus && (
            <span className={`curtail-foreman-pill ${foremanStatus.ping?.ok ? 'ok' : 'bad'}`}
              title={foremanStatus.power_control?.enabled
                ? `Foreman's native ramp is unlocked for: ${(foremanStatus.power_control?.unlocked_clients || []).join(', ')} — those sites ramp via Foreman, the rest self-ramp`
                : 'Ramping is handled by our server: miners are slept in timed steps across the ramp window. (Foreman\'s native ramp API is license-locked; if they ever unlock it, we switch automatically.)'}>
              {foremanStatus.ping?.ok
                ? `Foreman ✓${minerCount != null ? ` · ${minerCount} miners` : ''} · ${foremanStatus.power_control?.enabled ? 'native ramp ✓' : 'self-ramp'}`
                : `Foreman ✗ ${foremanStatus.ping?.error || `HTTP ${foremanStatus.ping?.status}`}`}
            </span>
          )}
          <button className="ghost" onClick={checkForeman} disabled={checking}
            title="Ping Foreman and pull a live miner summary">
            {checking ? 'Checking…' : 'Check Miners'}
          </button>
        </div>
      </div>

      <div className="curtail-form-grid">
        <div className="cf-field">
          <span className="cf-label">Start</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="datetime-local" value={start} onChange={e => setStart(e.target.value)} />
            <button type="button" className="ghost" onClick={startNow}
              title="Set start to right now — a start slightly in the past is fine, the backend fires it on its next 30-second pass">
              Now
            </button>
          </div>
        </div>
        <div className="cf-field">
          <span className="cf-label">End</span>
          <input type="datetime-local" value={end} onChange={e => setEnd(e.target.value)}
            disabled={noEnd} style={noEnd ? { opacity: 0.4 } : undefined} />
          <label className="cf-noend">
            <input type="checkbox" checked={noEnd} onChange={e => setNoEnd(e.target.checked)} />
            no end — stays curtailed until resumed by hand
          </label>
        </div>
        <div className="cf-field cf-narrow">
          <span className="cf-label">Ramp (min)</span>
          <input type="number" min="1" value={deadline} onChange={e => setDeadline(e.target.value)} />
        </div>
        <div className="cf-field">
          <span className="cf-label">Reason</span>
          <select value={reason} onChange={e => setReason(e.target.value)}>
            <option>MISO peak</option>
            <option>SWEC request</option>
            <option>Price spike</option>
            <option>Test</option>
            <option>Other</option>
          </select>
          {reason === 'Other' && (
            <input type="text" value={customReason} autoFocus
              placeholder="type the reason…" style={{ marginTop: 4 }}
              onChange={e => setCustomReason(e.target.value)} />
          )}
        </div>
        <div className="cf-field cf-wide">
          <span className="cf-label">Locations · empty = all</span>
          {locations.length > 0 ? (
            <details className="curtail-loc-picker" ref={locPickerRef}>
              <summary>
                {selectedSites.length === 0
                  ? 'All locations'
                  : `${selectedSites.length} of ${locations.length} selected`} ▾
              </summary>
              <div className="curtail-loc-drop">
                <div className="curtail-loc-tools">
                  <input type="text" placeholder="Filter…" value={locSearch}
                    onChange={e => setLocSearch(e.target.value)} />
                  <button className="ghost" onClick={() =>
                    setSelectedSites(prev => Array.from(new Set([...prev, ...shownMembers])))
                  }>Select shown</button>
                  <button className="ghost" onClick={() => setSelectedSites([])}>Clear</button>
                </div>
                <div className="curtail-loc-list">
                  {shownGroups.map(([g, members]) => {
                    if (members.length === 1) {
                      const l = members[0];
                      return (
                        <div className="loc-group" key={g}>
                          <label className="curtail-loc-item loc-group-head">
                            <input type="checkbox" checked={selectedSites.includes(l)}
                              onChange={() => toggleSite(l)} />
                            {l}
                          </label>
                        </div>
                      );
                    }
                    const selCount = members.filter(m => selectedSites.includes(m)).length;
                    const allIn = selCount === members.length;
                    const open = expandedGroups.has(g) || locSearch.trim() !== '';
                    return (
                      <div className="loc-group" key={g}>
                        <div className="loc-group-head">
                          <label className="curtail-loc-item">
                            <input type="checkbox" checked={allIn}
                              ref={el => { if (el) el.indeterminate = selCount > 0 && !allIn; }}
                              onChange={() => toggleGroup(members)} />
                            {g}
                            <span className="loc-count">
                              {selCount > 0 ? `${selCount}/${members.length}` : members.length}
                            </span>
                          </label>
                          <button type="button" className="loc-group-caret"
                            onClick={() => toggleExpand(g)}
                            title={open ? 'Collapse' : 'Show individual gauges'}>
                            {open ? '▾' : '▸'}
                          </button>
                        </div>
                        {open && (
                          <div className="loc-group-members">
                            {members.map(l => (
                              <label key={l} className="curtail-loc-item">
                                <input type="checkbox" checked={selectedSites.includes(l)}
                                  onChange={() => toggleSite(l)} />
                                {l}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </details>
          ) : (
            <input type="text" value={customSites}
              placeholder={locError ? `location list unavailable — type names, comma-separated` : 'e.g. Bondville 1, Arcola 2 North'}
              onChange={e => setCustomSites(e.target.value)} />
          )}
        </div>
        <div className="cf-field">
          <span className="cf-label">Note</span>
          <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="optional" />
        </div>
        <div className="cf-field cf-submit-cell">
          <label className="cf-dry" title={config?.is_admin
            ? 'Checked = log only. Uncheck to send real Foreman commands for this entry (master switch must also be off).'
            : 'Unchecking (going live) requires an admin account — sign in as Bill, Tony, or Elliott.'}>
            <input type="checkbox" checked={dryRunEntry}
              disabled={!config?.is_admin}
              onChange={e => setDryRunEntry(e.target.checked)} />
            dry run
          </label>
          <button className="primary cf-submit" onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Log curtailment'}
          </button>
        </div>
      </div>

      <div className="curtail-email-strip">
        <span className="cf-label" title="Everyone here gets the curtailment notice email when an entry is logged">
          Notice emails
        </span>
        <input type="text" value={emailInput}
          placeholder="tony@example.com, swec@example.com"
          onChange={e => setEmailInput(e.target.value)}
          disabled={!config?.is_admin}
          title={config?.is_admin ? 'Comma-separated recipient list' : 'Changing recipients requires an admin account'} />
        <button className="ghost" onClick={saveEmails} disabled={!config?.is_admin}>Save</button>
        <button className="ghost" onClick={sendTestEmail}
          disabled={!config?.is_admin || !emailCfg?.recipients?.length}
          title="Send a wiring-check email to the saved recipients">Send test</button>
        <span className="curtail-subnote">
          {emailCfg == null ? '' : emailCfg.gmail_ready
            ? 'sending via Gmail app ✓'
            : emailCfg.smtp_ready
              ? 'sending via SMTP'
              : 'no sender yet — copy the Gmail app credential + token JSONs to the server'}
          {emailStatus && ` · ${emailStatus}`}
        </span>
      </div>

      {selectedSites.length > 0 && (
        <div className="curtail-selected-strip">
          {siteGroups.map(([g, members]) => {
            const sel = members.filter(m => selectedSites.includes(m));
            if (sel.length === 0) return null;
            if (members.length > 1 && sel.length === members.length) {
              return (
                <span key={g} className="curtail-selected-chip" onClick={() => toggleGroup(members)}
                  title={`${members.join(', ')} — click to remove all`}>
                  {g} · all {members.length} ✕
                </span>
              );
            }
            return sel.map(s => (
              <span key={s} className="curtail-selected-chip" onClick={() => toggleSite(s)} title="Click to remove">
                {s} ✕
              </span>
            ));
          })}
        </div>
      )}
      {formError && <div className="curtail-error">{formError}</div>}

      {entries.length === 0 ? (
        <div className="curtail-empty">No curtailments logged yet.</div>
      ) : (
        <div className="curtail-list">
          {entries.map(e => (
            <div key={e.id} className={`curtail-row st-${e.status}`}>
              <div className="curtail-row-main">
                <div className="curtail-row-title">
                  {fmtRange(e.start, e.end)}
                  <span className={`badge ${{ scheduled: 'warning', active: 'danger', completed: 'normal' }[e.status] || 'muted'}`}>
                    {e.retroactive ? 'logged' : e.status}
                  </span>
                  {e.dry_run === false ? (
                    <span className="badge danger" title={dryRun
                      ? 'Logged as live, but the master dry-run switch is on — nothing fires until it\'s off.'
                      : 'Live entry — real Foreman commands fire at start/end.'}>
                      LIVE{dryRun ? ' (held)' : ''}
                    </span>
                  ) : (
                    <span className="badge muted" title="Dry run — Foreman commands are recorded, not sent.">dry</span>
                  )}
                </div>
                <div className="curtail-row-sub">
                  {e.sites?.length ? `${e.sites.length} location${e.sites.length === 1 ? '' : 's'}` : 'All locations'}
                  {' · '}{e.reason}{e.note ? ` — ${e.note}` : ''}
                  {' · logged '}{fmtStamp(e.created_at)}
                  {e.email?.sent && ` · emailed ${e.email.to}`}
                </div>
                {e.sites?.length > 0 && (
                  <div className="curtail-row-sites" title={e.sites.join(', ')}>
                    {e.sites.join(', ')}
                  </div>
                )}
                {foremanSummary(e, dryRun) && (
                  <div className="curtail-row-foreman">{foremanSummary(e, dryRun)}</div>
                )}
                <details className="curtail-detail">
                  <summary>detail</summary>
                  <pre>{JSON.stringify({ foreman: e.foreman, email: e.email, history: e.history }, null, 2)}</pre>
                </details>
              </div>
              <div className="curtail-row-actions">
                {e.status === 'scheduled' && (
                  <button onClick={() => doCancel(e.id)}>Cancel</button>
                )}
                {e.status === 'active' && (
                  <button className="primary" onClick={() => doResume(e.id)}
                    title="End this curtailment now and bring the miners back">Resume now</button>
                )}
                {(e.status === 'completed' || e.status === 'canceled') && (
                  <button className="ghost" onClick={() => doDelete(e.id)} title="Delete log entry">✕</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    <MinerFleet entries={entries} />
    </>
  );
}
